import { useEffect, useState, useCallback, useRef } from 'react';
import { 
  GeoLocation, 
  LocationUpdate, 
  LocationTrackingResult 
} from '@/types/nativeBridge.types';
import { startLocationTracking, stopLocationTracking, isNativeWebView } from '@/utils/nativeBridge';

export enum LocationTrackingStatus {
  IDLE = 'idle',
  STARTING = 'starting',
  TRACKING = 'tracking',
  WITHIN_RANGE = 'within_range',
  OUT_OF_RANGE = 'out_of_range',
  ERROR = 'error',
  STOPPED = 'stopped'
}

interface UseNativeLocationTrackingReturn {
  status: LocationTrackingStatus;
  locationUpdate: LocationUpdate | null;
  error: string | null;
  distance: number | null;
  withinRange: boolean;
  isTracking: boolean;
  startTracking: (targetLocation: GeoLocation, distanceThresholdYards?: number) => void;
  stopTracking: () => void;
  retryTracking: () => void;
}

/**
 * Hook for managing native location tracking
 * Handles communication with React Native WebView for location-based features
 */
export function useNativeLocationTracking(): UseNativeLocationTrackingReturn {
  const [status, setStatus] = useState<LocationTrackingStatus>(LocationTrackingStatus.IDLE);
  const [locationUpdate, setLocationUpdate] = useState<LocationUpdate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const targetLocationRef = useRef<GeoLocation | null>(null);

  /**
   * Start location tracking for a target location
   */
  const startTracking = useCallback((targetLocation: GeoLocation, distanceThresholdYards?: number) => {
    if (!isNativeWebView()) {
      console.warn('[LocationTracking] Not running in React Native WebView');
      setError('Location tracking is only available in the mobile app');
      setStatus(LocationTrackingStatus.ERROR);
      return;
    }

    console.log('[LocationTracking] Starting tracking for:', targetLocation, 'with threshold:', distanceThresholdYards);
    targetLocationRef.current = targetLocation;
    setStatus(LocationTrackingStatus.STARTING);
    setError(null);
    setLocationUpdate(null);
    
    startLocationTracking(targetLocation, distanceThresholdYards);
  }, []);

  /**
   * Stop location tracking
   */
  const stopTracking = useCallback(() => {
    if (!isNativeWebView()) return;

    console.log('[LocationTracking] Stopping tracking');
    stopLocationTracking();
    setStatus(LocationTrackingStatus.STOPPED);
    // Don't clear locationUpdate - preserve last location data for UI
    // setLocationUpdate(null);
    targetLocationRef.current = null;
  }, []);

  /**
   * Retry tracking with the same target location
   */
  const retryTracking = useCallback(() => {
    if (targetLocationRef.current) {
      console.log('[LocationTracking] Retrying tracking');
      // Clear previous location data and reset state
      setLocationUpdate(null);
      setError(null);
      setStatus(LocationTrackingStatus.STARTING);
      // Start tracking again
      startTracking(targetLocationRef.current);
    } else {
      console.warn('[LocationTracking] No target location to retry');
    }
  }, [startTracking]);

  /**
   * Helper to send logs to native for debugging
   */
  const logToNative = (message: string, data?: any) => {
    console.log(message, data);
    if (isNativeWebView()) {
      try {
        const webView = (window as any).ReactNativeWebView;
        if (webView) {
          webView.postMessage(JSON.stringify({
            type: 'web_log',
            payload: { message, data }
          }));
        }
      } catch {
        // Ignore if native logging fails
      }
    }
  };

  /**
   * Handle messages from React Native
   */
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        logToNative('[LocationTracking] Raw message received:', event.data);
        
        // Ignore non-string messages or messages that aren't JSON
        if (typeof event.data !== 'string') {
          logToNative('[LocationTracking] Ignoring non-string message');
          return;
        }

        // Try to parse as JSON, but silently ignore if it's not valid JSON
        // (e.g., messages from reCAPTCHA or other sources)
        let message;
        try {
          message = JSON.parse(event.data);
          logToNative('[LocationTracking] Parsed message:', message);
        } catch (err) {
          // Not a JSON message, ignore it
          logToNative('[LocationTracking] Failed to parse JSON, ignoring');
          return;
        }

        // Only process messages with a 'type' field (our native messages)
        if (!message.type) {
          logToNative('[LocationTracking] Message has no type field, ignoring');
          return;
        }

        logToNative('[LocationTracking] Processing message type:', message.type);
        switch (message.type) {
          case 'location_tracking_result': {
            const result = message.payload as LocationTrackingResult;
            if (result.success) {
              setStatus(LocationTrackingStatus.TRACKING);
              setError(null);
              console.log('[LocationTracking] Tracking started successfully');
            } else {
              setError(result.error || 'Failed to start location tracking');
              setStatus(LocationTrackingStatus.ERROR);
              console.error('[LocationTracking] Failed to start:', result.error);
            }
            break;
          }

          case 'location_update': {
            const update = message.payload as LocationUpdate;
            logToNative('[LocationTracking] Setting location update:', update);
            setLocationUpdate(update);
            if (update.withinRange) {
              logToNative('[LocationTracking] Setting status to WITHIN_RANGE');
              setStatus(LocationTrackingStatus.WITHIN_RANGE);
              console.log('[LocationTracking] Within range:', update.distance, 'yards');
            } else {
              logToNative('[LocationTracking] Setting status to OUT_OF_RANGE');
              setStatus(LocationTrackingStatus.OUT_OF_RANGE);
              console.log('[LocationTracking] Out of range:', update.distance, 'yards');
            }
            logToNative('[LocationTracking] Location update processed successfully');
            break;
          }

          case 'location_tracking_stopped': {
            setStatus(LocationTrackingStatus.STOPPED);
            // Don't clear locationUpdate - preserve last location data for UI
            console.log('[LocationTracking] Tracking stopped by native');
            break;
          }

          case 'error': {
            const payload = message.payload as { error?: string };
            const errorMsg = payload?.error || 'Unknown error';
            setError(errorMsg);
            setStatus(LocationTrackingStatus.ERROR);
            console.error('[LocationTracking] Error from native:', errorMsg);
            break;
          }
        }
      } catch (err) {
        console.error('[LocationTracking] Error parsing native message:', err);
      }
    };

    // Listen on both window and document for cross-platform support
    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage as any);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage as any);
    };
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (status === LocationTrackingStatus.TRACKING || 
          status === LocationTrackingStatus.WITHIN_RANGE || 
          status === LocationTrackingStatus.OUT_OF_RANGE) {
        stopTracking();
      }
    };
  }, [status, stopTracking]);

  return {
    status,
    locationUpdate,
    error,
    distance: locationUpdate?.distance ?? null,
    withinRange: locationUpdate?.withinRange ?? false,
    isTracking: status === LocationTrackingStatus.TRACKING || 
                status === LocationTrackingStatus.WITHIN_RANGE || 
                status === LocationTrackingStatus.OUT_OF_RANGE,
    startTracking,
    stopTracking,
    retryTracking
  };
}
